/**
 * BinQRSection - QR code printing for bins
 *
 * Extracted from BinManagementSection for the QR Codes tab.
 * Provides:
 * - Print QR codes for all bins of a type
 * - Reprint individual bin QR codes
 * - Print packing shelf QR code
 *
 * UX Requirements:
 * - Large touch targets (44px minimum)
 * - Loading states with spinners
 * - Error handling with toasts
 * - Accessibility (ARIA labels)
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { QRPreviewDialog } from './QRPrintLayout';
import {
  Loader2,
  QrCode,
  Info,
  Archive,
  MapPin,
  RefreshCw,
} from 'lucide-react';

export function BinQRSection() {
  const { selectedLocation } = useLocation();

  // Dialog state for QR preview
  const [regularQRDialogOpen, setRegularQRDialogOpen] = useState(false);
  const [oosQRDialogOpen, setOosQRDialogOpen] = useState(false);
  const [shelfQRDialogOpen, setShelfQRDialogOpen] = useState(false);
  const [singleBinQRDialogOpen, setSingleBinQRDialogOpen] = useState(false);

  // Single bin reprint state
  const [singleBinLabel, setSingleBinLabel] = useState('');

  const locationId = selectedLocation?.id ?? '';

  // Bin statistics query (for displaying counts)
  const binStatsQuery = trpc.bins.getBinStats.useQuery(
    { locationId },
    { enabled: !!locationId }
  );

  // QR code queries (lazy - only fetch when dialog opens)
  const regularQRQuery = trpc.bins.generateBinQRs.useQuery(
    { locationId, binType: 'REGULAR' },
    { enabled: regularQRDialogOpen && !!locationId }
  );

  const oosQRQuery = trpc.bins.generateBinQRs.useQuery(
    { locationId, binType: 'OOS' },
    { enabled: oosQRDialogOpen && !!locationId }
  );

  const shelfQRQuery = trpc.bins.generateShelfQR.useQuery(
    { locationId },
    { enabled: shelfQRDialogOpen && !!locationId }
  );

  // Single bin QR query (for reprint)
  const singleBinQRQuery = trpc.bins.generateSingleBinQR.useQuery(
    { locationId, label: singleBinLabel },
    {
      enabled: singleBinQRDialogOpen && !!locationId && !!singleBinLabel,
      retry: false, // Don't retry on not found
    }
  );

  // No location selected state
  if (!selectedLocation?.id) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        role="status"
        aria-label="No location selected"
      >
        <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" aria-hidden="true" />
        <p className="text-muted-foreground mb-2">
          Select a location from the header to print bin QR codes
        </p>
        <p className="text-sm text-muted-foreground">
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Tab</kbd> to navigation, then use{' '}
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Enter</kbd> to select a location
        </p>
      </div>
    );
  }

  const stats = binStatsQuery.data;
  const isLoading = binStatsQuery.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="ml-2 text-muted-foreground">Loading bin statistics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bin Statistics Summary */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg"
        role="region"
        aria-label="Bin statistics"
      >
        <div className="space-y-1 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">Regular Bins</p>
          <p className="text-3xl sm:text-2xl font-semibold">
            {stats?.regularCount ?? 0}
          </p>
        </div>
        <div className="space-y-1 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">OOS Bins</p>
          <p className="text-3xl sm:text-2xl font-semibold">
            {stats?.oosCount ?? 0}
          </p>
        </div>
      </div>

      <Separator />

      {/* Print Regular Bin QRs */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Archive className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Label className="text-base font-medium">Regular Bin QR Codes</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Print QR codes for all {stats?.regularCount ?? 0} regular bins at this location.
        </p>
        <QRPreviewDialog
          qrCodes={regularQRQuery.data ?? []}
          binType="REGULAR"
          open={regularQRDialogOpen}
          onOpenChange={setRegularQRDialogOpen}
        >
          <Button
            variant="outline"
            className="h-12 gap-2"
            disabled={(stats?.regularCount ?? 0) === 0 || regularQRQuery.isFetching}
            aria-label="Print regular bin QR codes"
            aria-busy={regularQRQuery.isFetching}
          >
            {regularQRQuery.isFetching ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <QrCode className="h-5 w-5" aria-hidden="true" />
                <span>Print All Regular Bin QRs</span>
              </>
            )}
          </Button>
        </QRPreviewDialog>
      </div>

      <Separator />

      {/* Print OOS Bin QRs */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Archive className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Label className="text-base font-medium">OOS Bin QR Codes</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Print QR codes for all {stats?.oosCount ?? 0} OOS bins at this location.
        </p>
        <QRPreviewDialog
          qrCodes={oosQRQuery.data ?? []}
          binType="OOS"
          open={oosQRDialogOpen}
          onOpenChange={setOosQRDialogOpen}
        >
          <Button
            variant="outline"
            className="h-12 gap-2"
            disabled={(stats?.oosCount ?? 0) === 0 || oosQRQuery.isFetching}
            aria-label="Print OOS bin QR codes"
            aria-busy={oosQRQuery.isFetching}
          >
            {oosQRQuery.isFetching ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <QrCode className="h-5 w-5" aria-hidden="true" />
                <span>Print All OOS Bin QRs</span>
              </>
            )}
          </Button>
        </QRPreviewDialog>
      </div>

      <Separator />

      {/* Packing Shelf QR */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Label className="text-base font-medium">Packing Shelf QR</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Single-line orders bypass bins and go directly to the packing shelf.
          Print this QR code for your packing shelf signage.
        </p>
        <QRPreviewDialog
          qrCodes={shelfQRQuery.data ? [shelfQRQuery.data] : []}
          binType="SHELF"
          open={shelfQRDialogOpen}
          onOpenChange={setShelfQRDialogOpen}
        >
          <Button
            variant="outline"
            className="h-12 gap-2"
            aria-label="Print packing shelf QR code"
            aria-busy={shelfQRQuery.isFetching}
          >
            {shelfQRQuery.isFetching ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <QrCode className="h-5 w-5" aria-hidden="true" />
                <span>Print Shelf QR</span>
              </>
            )}
          </Button>
        </QRPreviewDialog>
      </div>

      <Separator />

      {/* Single Bin Reprint */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Label className="text-base font-medium">Reprint Single Bin QR</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Reprint a QR code for a specific bin label (e.g., BIN-042 or OOS-003)
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="single-bin-label" className="text-sm text-muted-foreground">
              Bin label to reprint
            </Label>
            <Input
              id="single-bin-label"
              type="text"
              placeholder="BIN-001 or OOS-001"
              value={singleBinLabel}
              onChange={(e) => setSingleBinLabel(e.target.value.toUpperCase())}
              className="h-12 text-center text-lg font-mono uppercase"
              aria-describedby="single-bin-label-help"
            />
            <span id="single-bin-label-help" className="sr-only">
              Enter a bin label like BIN-001 or OOS-001
            </span>
          </div>
          <div className="sm:self-end">
            <QRPreviewDialog
              qrCodes={singleBinQRQuery.data ? [singleBinQRQuery.data] : []}
              binType={singleBinLabel.startsWith('OOS') ? 'OOS' : 'REGULAR'}
              open={singleBinQRDialogOpen}
              onOpenChange={(open) => {
                setSingleBinQRDialogOpen(open);
                if (!open) {
                  // Clear any stale error state when closing
                  singleBinQRQuery.refetch();
                }
              }}
            >
              <Button
                variant="outline"
                className="h-12 gap-2"
                disabled={!singleBinLabel || !/^(BIN|OOS)-\d{3,4}$/.test(singleBinLabel)}
                aria-label={`Reprint QR for ${singleBinLabel || 'bin'}`}
              >
                {singleBinQRQuery.isFetching ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <QrCode className="h-5 w-5" aria-hidden="true" />
                )}
                Reprint QR
              </Button>
            </QRPreviewDialog>
          </div>
        </div>
        {singleBinQRQuery.isError && (
          <p className="text-sm text-destructive">
            {singleBinQRQuery.error?.message || 'Bin not found'}
          </p>
        )}
      </div>

      {/* Info box */}
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <strong>QR codes</strong> contain the bin label in format{' '}
              <code className="bg-background px-1 rounded">THP://BIN:BIN-001</code>
            </p>
            <p>
              <strong>Tip:</strong> Use a label printer for best results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
